/**
 * DEMO DATA — recording manifest.
 *
 * ⚠️  PHASE 1 (CLIENT DEMO) ONLY. Real call data arrives in the next phase;
 *     see ../demo-mode.ts for how this is switched off.
 *
 * These are real MP3 recordings copied into `apps/web/public/demo-recordings/`,
 * so the demo can actually PLAY a call rather than miming it. The metadata is
 * recovered from the KC2 filenames, which encode
 * `rec_<did>_<caller>_<uuid>_<yyyymmddhhmmss>`.
 *
 * `approxDurationSec` is derived from file size at 192 kbps CBR
 * (bytes / 24000), which is what these files actually are — an earlier 64 kbps
 * assumption made every estimate 3x too long, and the monitor seeked past the
 * end of the file and played silence.
 *
 * It is only used to cap the SIMULATED call length. The player itself seeks
 * against the audio element's REAL `duration`, so a wrong value here can never
 * again produce a dead playhead.
 */

export interface DemoRecording {
  /** Original KC2 call UUID — reused as the demo call id so the two line up. */
  callId: string
  /** Public URL, served straight out of `apps/web/public`. */
  file: string
  /** Dialled DID the call arrived on. */
  did: string
  /** E.164 caller line identifier. */
  callerCLI: string
  /** When the original call was recorded (ISO8601). */
  recordedAt: string
  approxDurationSec: number
}

export const DEMO_RECORDINGS: DemoRecording[] = [
  {
    callId: '0faceafa-84ee-10c2-21ee-2f90238b3e64',
    file: '/demo-recordings/call-0faceafa.mp3',
    did: '61861173864',
    callerCLI: '+1161884078888',
    recordedAt: '2026-07-31T04:28:29Z',
    approxDurationSec: 170,
  },
  {
    callId: '4564e654-b796-3fca-c00f-1c1cae80ebf2',
    file: '/demo-recordings/call-4564e654.mp3',
    did: '61861173864',
    callerCLI: '+1161738585999',
    recordedAt: '2026-07-31T05:16:01Z',
    approxDurationSec: 22,
  },
  {
    callId: '58700cf3-04e0-e852-e7f3-bc210ebd9603',
    file: '/demo-recordings/call-58700cf3.mp3',
    did: '61861173864',
    callerCLI: '+1161418883665',
    recordedAt: '2026-07-30T05:48:56Z',
    approxDurationSec: 13,
  },
  {
    callId: '76b58d3e-50de-fcfc-8ebb-2253c28277c2',
    file: '/demo-recordings/call-76b58d3e.mp3',
    did: '61861173864',
    callerCLI: '+1161882122811',
    recordedAt: '2026-07-30T05:46:30Z',
    approxDurationSec: 127,
  },
  {
    callId: '97c57617-26fc-5b20-9e50-90e6a6903952',
    file: '/demo-recordings/call-97c57617.mp3',
    did: '61861173864',
    callerCLI: '+1161883506688',
    recordedAt: '2026-07-31T03:45:10Z',
    approxDurationSec: 124,
  },
  {
    callId: '9e04faeb-802a-9041-df27-292e33705dd4',
    file: '/demo-recordings/call-9e04faeb.mp3',
    did: '61861173864',
    callerCLI: '+1161881007500',
    recordedAt: '2026-07-30T05:36:34Z',
    approxDurationSec: 101,
  },
  {
    callId: 'ab9c4930-0103-e2b4-ff96-0353fa1d905f',
    file: '/demo-recordings/call-ab9c4930.mp3',
    did: '61861173864',
    callerCLI: '+1161881595588',
    recordedAt: '2026-07-30T05:33:01Z',
    approxDurationSec: 196,
  },
  {
    callId: 'af44a164-66b6-5615-637b-8cc928750e82',
    file: '/demo-recordings/call-af44a164.mp3',
    did: '61861173864',
    callerCLI: '+1161882172500',
    recordedAt: '2026-07-30T05:30:40Z',
    approxDurationSec: 26,
  },
  {
    callId: 'b61f917c-610a-c105-9766-1dd4d916bb1d',
    file: '/demo-recordings/call-b61f917c.mp3',
    did: '61861173864',
    callerCLI: '+1161882138803',
    recordedAt: '2026-07-30T05:42:35Z',
    approxDurationSec: 70,
  },
  {
    callId: 'b7e55fed-8c2b-873e-8867-6c63110f9007',
    file: '/demo-recordings/call-b7e55fed.mp3',
    did: '61861173864',
    callerCLI: '+1161870747800',
    recordedAt: '2026-07-30T05:19:22Z',
    approxDurationSec: 20,
  },
  {
    callId: 'dba21e5c-0829-d381-5fcd-def55ae8a1c2',
    file: '/demo-recordings/call-dba21e5c.mp3',
    did: '61861173864',
    callerCLI: '+1161870711675',
    recordedAt: '2026-07-31T03:26:05Z',
    approxDurationSec: 50,
  },
  {
    callId: 'dc5bc2a4-fba6-8419-8ab7-75405ce81706',
    file: '/demo-recordings/call-dc5bc2a4.mp3',
    did: '61861173864',
    callerCLI: '+1161882172500',
    recordedAt: '2026-07-30T05:30:40Z',
    approxDurationSec: 100,
  },
  {
    callId: 'e9b1837a-a670-17e3-af79-8fcd97ba3f08',
    file: '/demo-recordings/call-e9b1837a.mp3',
    did: '61861173864',
    callerCLI: '+1161460832261',
    recordedAt: '2026-07-30T05:13:50Z',
    approxDurationSec: 15,
  },
]

/** Stable lookup for the player. */
export const RECORDING_BY_CALL_ID = new Map<string, DemoRecording>(
  DEMO_RECORDINGS.map((r) => [r.callId, r]),
)
